
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FileUpload } from "./BaseForm";
import { SharedFieldsFormData, FormProps } from "@/types/forms";

type SharedFieldsFormProps = FormProps<SharedFieldsFormData>;

const SharedFieldsForm = ({ onSubmit, initialData = {}, buttonText = "Continue to Product Details" }: SharedFieldsFormProps) => {
  const [formData, setFormData] = useState<SharedFieldsFormData>({
    tradeLicense: initialData.tradeLicense || null,
  });
  const [noTradeLicense, setNoTradeLicense] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only require trade license if checkbox is not checked
    if (!formData.tradeLicense && !noTradeLicense) {
      newErrors.tradeLicense = "Please upload your trade license or check the box below";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof SharedFieldsFormData>(field: K, value: SharedFieldsFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setNoTradeLicense(checked);
    if (checked) {
      // Clear trade license file when checkbox is checked
      setFormData(prev => ({ ...prev, tradeLicense: null }));
      // Clear any existing errors
      setErrors(prev => ({ ...prev, tradeLicense: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">General Business Information</h3>
        <p className="text-gray-600">This information will be used for all your insurance products.</p>
      </div>

      <FileUpload
        label="Upload Trade License"
        accept=".pdf,.jpg,.jpeg,.png"
        required={false}
        value={formData.tradeLicense}
        onChange={(file) => updateField('tradeLicense', file)}
        disabled={noTradeLicense}
      />

      <div className="flex items-center space-x-2">
        <Checkbox
          id="no-trade-license"
          checked={noTradeLicense}
          onCheckedChange={handleCheckboxChange}
        />
        <label
          htmlFor="no-trade-license"
          className="text-sm text-gray-700 cursor-pointer"
        >
          I don't have my trade license with me
        </label>
      </div>

      {errors.tradeLicense && <p className="text-red-500 text-sm">{errors.tradeLicense}</p>}

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default SharedFieldsForm;
